import React, { useState, useEffect, useMemo, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import { toast } from "react-toastify";

import { SpinnerSmall, InlineSpinner } from "components/Spinner";
import Modal from "react-bootstrap/Modal";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import sortBy from "lodash/sortBy";
import {
  PhGridPagination,
  PhGridFilter,
  usePagination,
  PhGridSort,
} from "components/PhGrid";

import dompurify from "dompurify";
import "react-toastify/dist/ReactToastify.css";
import '../../assets/_master.scss';
import { cookieAttributes } from "util/resumeCookieSettings";


/* ---------------- Icons & helpers (restore these to avoid modal crash) -------------- */

function PhysicianIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="50" height="50">
      <path fill="#3c763d" d="M96 128l0-57.8c0-13.3 8.3-25.3 20.8-30l96-36c7.2-2.7 15.2-2.7 22.5 0l96 36c12.5 4.7 20.8 16.6 20.8 30l0 57.8-.3 0c.2 2.6 .3 5.3 .3 8l0 40c0 70.7-57.3 128-128 128s-128-57.3-128-128l0-40c0-2.7 .1-5.4 .3-8l-.3 0zm48 48c0 44.2 35.8 80 80 80s80-35.8 80-80l0-16-160 0 0 16zM111.9 327.7c10.5-3.4 21.8 .4 29.4 8.5l71 75.5c6.3 6.7 17 6.7 23.3 0l71-75.5c7.6-8.1 18.9-11.9 29.4-8.5C401 348.6 448 409.4 448 481.3c0 17-13.8 30.7-30.7 30.7L30.7 512C13.8 512 0 498.2 0 481.3c0-71.9 47-132.7 111.9-153.6zM208 48l0 16-16 0c-4.4 0-8 3.6-8 8l0 16c0 4.4 3.6 8 8 8l16 0 0 16c0 4.4 3.6 8 8 8l16 0c4.4 0 8-3.6 8-8l0-16 16 0c4.4 0 8-3.6 8-8l0-16c0-4.4-3.6-8-8-8l-16 0 0-16c0-4.4-3.6-8-8-8l-16 0c-4.4 0-8 3.6-8 8z" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="50" height="50" style={{ marginRight: "2%", marginLeft: "2%" }}>
      <path fill="#3c763d" d="M406.6 374.6l96-96c12.5-12.5 12.5-32.8 0-45.3l-96-96c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224l-293.5 0 41.4-41.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-96 96c-12.5 12.5-12.5 32.8 0 45.3l96 96c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 288l293.5 0-41.4 41.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0z" />
    </svg>
  );
}

function FacilityIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="50" height="50">
      <path fill="#3c763d" d="M543.8 287.6c17 0 32-14 32-32.1c1-9-3-17-11-24L309.5 7c-6-5-14-7-21-7s-15 1-22 8L10 231.5c-7 7-10 15-10 24c0 18 14 32.1 32 32.1l32 0 0 160.4c0 35.3 28.7 64 64 64l320.4 0c35.5 0 64.2-28.8 64-64.3l-.7-160.2 32 0zM256 208c0-8.8 7.2-16 16-16l32 0c8.8 0 16 7.2 16 16l0 48 48 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-48 0 0 48c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-48-48 0c-8.8 0-16-7.2-16-16l0-32c0-8.8 7.2-16 16-16l48 0 0-48z" />
    </svg>
  );
}

function SelectedButton() {
  return (
    <div style={{ margin: "auto auto 0 auto", width: "maxContent" }}>
      <button className="btn btn-primary m-2 float-right" style={{ width: "100%" }}>
        <svg xmlns="http://www.w3.org/2000/svg" height="14" width="12.25" viewBox="0 0 448 512">
          <path fill="#ffffff" d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" />
        </svg>{" "}
        Selected
      </button>
    </div>
  );
}

/* ---------------- Utilities ---------------- */

function translateDateFormat(dateStr) {// converts
  const monthDate = new Date();
  monthDate.setMonth(Number(dateStr.slice(5,7)) - 1);
  
  const monthStr = monthDate.toLocaleString('en-US', {
    month: 'long',
  });

  return `${monthStr} ${Number(dateStr.slice(8,10))}, ${dateStr.slice(0,4)}`;
}

/* -------------------------------- Row -------------------------------- */

function MonitorRow(props) {
  const {
    monitor,
    availableCommunities,
    availableWatchers,
    init,
    setPageState,
    setPreviewModalActive,
    setPreviewModalReport,
    modalReportType,
    setModalReportType,
  } = props;

  const [expanded, setExpanded] = React.useState(false);

  const watchersText = (monitor?.watchers || [])
    .map((id) => {
      const u = (availableWatchers || []).find((aw) => aw.id === id);
      if (!u) return null;
      const name = u.name || u.email || "User";
      return `${name}${u.email ? ` (${u.email})` : ""}`;
    })
    .filter(Boolean)
    .join(", ");

  return (
    <React.Fragment key={monitor?.id}>
      <tr>
        <th scope="row">{monitor?.name || <span className="text-danger">Name not found</span>}</th>

        <td>
          <OverlayTrigger
            placement="left"
            overlay={
              <Tooltip>
                {monitor?.reportType === "Physician to Facility" && "Physician community to facility community"}
                {monitor?.reportType === "Physician to Physician" && "Physician community to physician community"}
                {monitor?.reportType === "Facility to Facility" && "Facility community to facility community"}
                {!monitor?.reportType && "Undefined report type"}
              </Tooltip>
            }
          >
            <label className="col-form-label p-0">{monitor?.reportType || "Undefined report type"}</label>
          </OverlayTrigger>
        </td>

        <td>{monitor?.monitoringcommunity?.name || <span className="text-danger">Community not found</span>}</td>
        <td>{monitor?.targetcommunity?.name || <span className="text-danger">Community not found</span>}</td>

        <td>{watchersText || <span className="text-danger">No watchers</span>}</td>

        <td>{monitor?.frequency || <span className="text-danger">Frequency not found</span>}</td>

        <td>
          {monitor?.isactive ? (
            <span className="badge badge-pill badge-success">Active</span>
          ) : (
            <span className="badge badge-pill badge-warning">Inactive</span>
          )}
        </td>

        <td>
          {monitor?.modified && translateDateFormat(monitor.modified)
            ? translateDateFormat(monitor.modified)
            : <span className="text-danger">Date not found</span>}
        </td>

        <td>
          <div style={{ whiteSpace: "nowrap" }}>
            <button
              type="button"
              title="Edit"
              className={`btn btn-${expanded ? "secondary" : "primary"} font-weight-bold m-1 px-2 py-1`}
              onClick={() => setExpanded(!expanded)}
            >
              Edit
            </button>

            <button
              type="button"
              title="Preview"
              className="btn btn-success font-weight-bold m-1 px-2 py-1"
              disabled={!monitor?.monitoringcommunity?.name || !monitor?.targetcommunity?.name}
              onClick={() => {
                setPreviewModalReport(monitor);
                setPreviewModalActive(true);
              }}
            >
              Preview
            </button>
          </div>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan="9">
            <div className="card container">
              <MonitorForm
                monitor={monitor}
                availableCommunities={availableCommunities}
                availableWatchers={availableWatchers}
                setFormContainerOpen={setExpanded}
                init={init}
                setPageState={setPageState}
                modalReportType={modalReportType || monitor?.reportType}
                setModalReportType={setModalReportType}
              />
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

/* ------------------------------- Form -------------------------------- */

function MonitorForm({
  monitor,
  availableCommunities,
  availableWatchers,
  setFormContainerOpen,
  init,
  setPageState,
  modalReportType,
  setModalReportType,
  profile,
}) {
  const [showInfo, setShowInfo] = React.useState(false);
  const [selectedReportType, setSelectedReportType] = React.useState(modalReportType);
  const [selectedMonitoringCommunity, setSelectedMonitoringCommunity] = React.useState(null);
  const [selectedTargetCommunity, setSelectedTargetCommunity] = React.useState(null);
  const [selectedWatchers, setSelectedWatchers] = React.useState([]);
  const [selectedFrequency, setSelectedFrequency] = React.useState(null);
  const [selectedStatus, setSelectedStatus] = React.useState(null);
  const [monitorName, setMonitorName] = React.useState("");
  const [community1Members, setCommunity1Members] = React.useState([]);
  const [community2Members, setCommunity2Members] = React.useState([]);

  const validateName = (str) => /^[0-9A-Za-z_.-\s]+$/.test(str) && str.length < 50;

  const filterCommunitiesByEntityType = (communities, entityType) => {
    if (entityType === "3") return communities;
    return (communities || []).filter((c) => c.entitytype == entityType);
  };

  React.useEffect(() => {
    if (monitor) {
      setSelectedReportType(monitor.reportType);
      setSelectedMonitoringCommunity(monitor.monitoringcommunity);
      setSelectedTargetCommunity(monitor.targetcommunity);
      setSelectedWatchers(monitor.subscribers || []);
      setSelectedFrequency(monitor.frequency);
      setSelectedStatus(monitor.isactive);
      setMonitorName(monitor.name || "");
    }
  }, [monitor]);

  const payloadObj = React.useMemo(() => {
    const body = {};

    if (monitor) {
      if (monitor.reportType !== selectedReportType) body.reportType = selectedReportType;
      if (monitor.monitoringcommunity !== selectedMonitoringCommunity)
        body.monitoringcommunity = selectedMonitoringCommunity?.id;
      if (monitor.targetcommunity !== selectedTargetCommunity)
        body.targetcommunity = selectedTargetCommunity?.id;

      const before = new Set(monitor.subscribers || []);
      const after = new Set(selectedWatchers || []);
      if (before.size !== after.size || ![...before].every((x) => after.has(x))) {
        if ((selectedWatchers || []).filter(Boolean).length > 0) {
          body.subscribers = selectedWatchers;
        }
      }

      if (monitor.frequency !== selectedFrequency) body.frequency = selectedFrequency;
      if (monitor.isactive !== selectedStatus) body.isactive = selectedStatus;
      if (monitor.name !== monitorName) body.name = monitorName;

      if (Object.keys(body).length > 0 && Object.values(body).every((v) => v !== "" && v !== undefined)) {
        return body;
      }
      return null;
    } else {
      body.reportType = selectedReportType;
      body.monitoringcommunity = selectedMonitoringCommunity?.id;
      body.targetcommunity = selectedTargetCommunity?.id;
      body.subscribers = selectedWatchers;
      body.frequency = selectedFrequency;
      body.name = monitorName;
      if (
        body.reportType &&
        body.monitoringcommunity &&
        body.targetcommunity &&
        Array.isArray(body.subscribers) &&
        body.subscribers.filter(Boolean).length > 0 &&
        (body.frequency === "Quarterly" || body.frequency === "Monthly") &&
        body.name &&
        validateName(body.name)
      ) {
        return body;
      }
      return null;
    }
  }, [
    monitor,
    selectedReportType,
    selectedMonitoringCommunity,
    selectedTargetCommunity,
    selectedWatchers,
    selectedFrequency,
    selectedStatus,
    monitorName,
  ]);

  async function listCommunityMembers(memberIds, first) {
    try {
      await Auth.currentAuthenticatedUser();
      const npiData = await API.post("NpiApi", "/npi_t/search", {
        body: { include_compare: false, npis: memberIds || [] },
      });
      const names = (npiData?.data || []).map((npi) => npi?.nppes?.names?.name).filter(Boolean);
      if (first) setCommunity1Members(names);
      else setCommunity2Members(names);
    } catch {
      if (first) setCommunity1Members([]);
      else setCommunity2Members([]);
    }
  }

  return (
    <>
      <div className="row">
        <div className="col-3">
          <button
            className="btn btn-outline-success btn-sm font-weight-bold mr-1 mt-2"
            onClick={() => setShowInfo((s) => !s)}
          >
            {showInfo ? "Hide " : "Show "} Additional Info
          </button>
        </div>
        <div className="col-9">
          {showInfo && (
            <p className="mt-1">
              An Aware Report allows a user to identify two communities from the TEAM platform to measure market share
              and volume month over month. Once set, the user can choose watchers and a monthly/quarterly cadence to
              receive an interactive dashboard by email.
            </p>
          )}
        </div>
      </div>

      {/* Community 1 */}
      <div className="form-group row p-3 mb-0">
        <OverlayTrigger placement="left" overlay={<Tooltip>Select Community to monitor</Tooltip>}>
          <label className="col-3 col-form-label p-0">
            {(modalReportType === "Physician to Physician" || modalReportType === "Physician to Facility") &&
              "Physician Community:"}
            {modalReportType === "Facility to Facility" && "Facility Community:"}
          </label>
        </OverlayTrigger>
        <div className="col-4">
          <select
            className="form-control"
            disabled={!!monitor}
            value={selectedMonitoringCommunity?.id || ""}
            onChange={async (e) => {
              const c = (availableCommunities || []).find((com) => com.id === e.target.value);
              setSelectedMonitoringCommunity(c || null);
              await listCommunityMembers(c?.members || [], true);
            }}
          >
            {(modalReportType === "Physician to Physician" || modalReportType === "Physician to Facility") && (
              <option value="">-- Select Physician Community --</option>
            )}
            {modalReportType === "Facility to Facility" && <option value="">-- Select Facility Community --</option>}

            {(modalReportType === "Physician to Physician" || modalReportType === "Physician to Facility") &&
              filterCommunitiesByEntityType(availableCommunities || [], "1").map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}

            {modalReportType === "Facility to Facility" &&
              filterCommunitiesByEntityType(availableCommunities || [], "2").map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
          </select>

          {selectedMonitoringCommunity && (
            <div>
              <p className="mt-3">
                <a
                  className="btn btn-sm btn-outline-success"
                  href={`https://${process.env.REACT_APP_TEAM_DOMAIN}/community/${dompurify.sanitize(
                    selectedMonitoringCommunity.id
                  )}/home`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View/Edit {selectedMonitoringCommunity.name} in TEAM
                </a>
              </p>
            </div>
          )}
        </div>

        {selectedMonitoringCommunity && !monitor && (
          <div className="col border p-2 ml-5">
            <h1 style={{ fontSize: "18px" }}>
              <u>Community 1 Members:</u> ({selectedMonitoringCommunity.members.length}{" "}
              {selectedMonitoringCommunity.members.length > 1 ? "members" : "member"})
            </h1>
            <div style={{ maxHeight: "150px", overflow: "auto" }}>
              {community1Members.length > 0 ? (
                community1Members.reduce((p, c, i) => [p, <br key={`${c}-${i}`} />, c])
              ) : (
                <InlineSpinner />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Community 2 */}
      <div className="form-group row p-3 mb-0">
        <OverlayTrigger placement="left" overlay={<Tooltip>Select target community to monitor the first community's metrics</Tooltip>}>
          <label className="col-3 col-form-label p-0">
            {modalReportType === "Physician to Physician" && "Physician Community:"}
            {(modalReportType === "Physician to Facility" || modalReportType === "Facility to Facility") &&
              "Facility Community:"}
          </label>
        </OverlayTrigger>
        <div className="col-4">
          <select
            className="form-control"
            disabled={!!monitor}
            value={selectedTargetCommunity?.id || ""}
            onChange={async (e) => {
              const c = (availableCommunities || []).find((com) => com.id === e.target.value);
              setSelectedTargetCommunity(c || null);
              await listCommunityMembers(c?.members || [], false);
            }}
          >
            {modalReportType === "Physician to Physician" && <option value="">-- Select Physician Community --</option>}
            {(modalReportType === "Physician to Facility" || modalReportType === "Facility to Facility") && (
              <option value="">-- Select Facility Community --</option>
            )}

            {modalReportType === "Physician to Physician" &&
              filterCommunitiesByEntityType(availableCommunities || [], "1").map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}

            {(modalReportType === "Physician to Facility" || modalReportType === "Facility to Facility") &&
              filterCommunitiesByEntityType(availableCommunities || [], "2").map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
          </select>

          {selectedTargetCommunity && (
            <div>
              <p className="mt-3">
                <a
                  className="btn btn-sm btn-outline-success"
                  href={`https://${process.env.REACT_APP_TEAM_DOMAIN}/community/${dompurify.sanitize(
                    selectedTargetCommunity.id
                  )}/home`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View/Edit {selectedTargetCommunity.name} in TEAM
                </a>
              </p>
            </div>
          )}
        </div>

        {selectedTargetCommunity && !monitor && (
          <div className="col border p-2 ml-5">
            <h1 style={{ fontSize: "18px" }}>
              <u>Community 2 Members:</u> ({selectedTargetCommunity.members.length}{" "}
              {selectedTargetCommunity.members.length > 1 ? "members" : "member"})
            </h1>
            <div style={{ maxHeight: "150px", overflow: "auto" }}>
              {community2Members.length > 0 ? (
                community2Members.reduce((p, c, i) => [p, <br key={`${c}-${i}`} />, c])
              ) : (
                <InlineSpinner />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Watchers */}
      <div className="form-group row p-3 mb-0">
        <OverlayTrigger placement="left" overlay={<Tooltip>Select watchers within your account to receive reports by email</Tooltip>}>
          <label className="col-3 col-form-label p-0">
            Watchers:<br />ctrl + click to select multiple
          </label>
        </OverlayTrigger>

        <select
          className="form-control col-9"
          value={selectedWatchers || []}
          onChange={(e) => {
            const values = Array.from(e.target.options).filter((o) => o.selected).map((o) => o.value);
            setSelectedWatchers(values);
          }}
          multiple
        >
          <option value="">-- Select Watchers --</option>
          {(availableWatchers || []).map((w) => (
            <option key={w.id} value={w.id}>{w.name} ({w.email})</option>
          ))}
        </select>
      </div>

      {/* Frequency */}
      <div className="form-group row p-3 mb-0">
        <OverlayTrigger placement="left" overlay={<Tooltip>Select frequency</Tooltip>}>
          <label className="col-3 col-form-label p-0">Frequency:</label>
        </OverlayTrigger>
        <select className="form-control col-9" value={selectedFrequency || ""} onChange={(e) => setSelectedFrequency(e.target.value)}>
          <option value="">-- Select Frequency --</option>
          <option value="Quarterly">Quarterly</option>
          <option value="Monthly">Monthly</option>
        </select>
      </div>

      {/* Status (edit only) */}
      {monitor && (
        <div className="form-group row p-3 mb-0">
          <OverlayTrigger placement="left" overlay={<Tooltip>Set monitor status</Tooltip>}>
            <label className="col-3 col-form-label p-0">Status:</label>
          </OverlayTrigger>
          <select
            className="form-control col-9"
            value={selectedStatus ? "true" : "false"}
            onChange={(e) => setSelectedStatus(e.target.value === "true")}
          >
            <option value="">-- Select Status --</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      )}

      {/* Name */}
      <div className="form-group row p-3 mb-0">
        <OverlayTrigger placement="left" overlay={<Tooltip>Name the Aware report</Tooltip>}>
          <label className="col-3 col-form-label p-0">Name:</label>
        </OverlayTrigger>
        <input
          type="text"
          className={`form-control col-9 ${monitorName && !validateName(monitorName) ? "is-invalid" : ""}`}
          value={monitorName}
          onChange={(e) => setMonitorName(e.target.value)}
          placeholder="name the Aware report"
        />
        {monitorName && !validateName(monitorName) && (
          <div className="offset-3 invalid-feedback">
            Must be under 50 characters and use only letters, numbers, hyphens, underscores, periods and spaces.
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="row">
        <div className="col text-right">
          {/* SAVE — close modal immediately, then run calls and refresh */}
          <button
            className="btn btn-primary m-1"
            disabled={!payloadObj}
            onClick={async () => {
              setPageState("Saving...");
              if (setFormContainerOpen) setFormContainerOpen(false); // close modal first

              try {
                await Auth.currentAuthenticatedUser();

                if (monitor) {
                  await API.patch("MonitoringServiceApi", `/monitor/${monitor.id}`, { body: payloadObj });
                } else {
                  await API.post("MonitoringServiceApi", "/monitor", { body: { ...payloadObj, profile } });
                }

                // fire-and-forget refresh; init() will clear pageState
                init();
              } catch (e) {
                console.log("save failure", e);
                setPageState(null);
              }
            }}
          >
            {monitor ? "Save Edits" : "Save New"}
          </button>

          {/* DELETE — close modal immediately, then run calls and refresh */}
          {monitor && (
            <button
              className="btn btn-danger m-1"
              onClick={async () => {
                setPageState("Deleting...");
                if (setFormContainerOpen) setFormContainerOpen(false); // close modal first

                try {
                  await Auth.currentAuthenticatedUser();
                  await API.del("MonitoringServiceApi", `/monitor/${monitor.id}`);
                  init(); // fire-and-forget
                } catch (e) {
                  console.log("delete failure", e);
                  setPageState(null);
                }
              }}
            >
              Delete
            </button>
          )}

          <button
            className="btn btn-secondary m-1"
            onClick={() => {
              if (setFormContainerOpen) setFormContainerOpen(false);
              setModalReportType("Physician to Facility");
              setPageState(null);
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}

/* -------------------------------- Page -------------------------------- */

function MonitoringServicePage(props) {
  const {
    paginationOffset,
    setPaginationOffset,
    paginationSize,
    setPaginationSize,
    filterString,
    setFilterString,
    sortColumn,
    setSortColumn,
    sortKey,
    setSortKey,
    sortDirection,
    setSortDirection,
  } = usePagination();

  const [profile, setProfile] = useState(null);

  const [monitors, setMonitors] = useState(null);
  const [availableCommunities, setAvailableCommunities] = useState(null);
  const [availableWatchers, setAvailableWatchers] = useState(null);

  const [reportTypeModalActive, setReportTypeModalActive] = useState(false);
  const [creationModalActive, setCreationModalActive] = useState(false);
  const [previewModalActive, setPreviewModalActive] = useState(false);

  const [modalReportType, setModalReportType] = useState("Physician to Facility");
  const [modalMonitor, setModalMonitor] = useState(null);
  const [modalTarget, setModalTarget] = useState(null);
  const [modalWatchers, setModalWatchers] = useState(null);
  const [modalFrequency, setModalFrequency] = useState(null);
  const [modalMonitoringServiceName, setModalMonitoringServiceName] = useState(null);
  const [pageState, setPageState] = useState("Loading");

  const [previewModalReport, setPreviewModalReport] = useState(null);

  const [ready, setReady] = useState(false);
  const initInFlight = useRef(false);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const authListener = async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setProfile(null);
          setMonitors(null);
          setAvailableCommunities(null);
          setAvailableWatchers(null);
          setPageState("Loading");
        }
      }
    };

    Hub.listen("auth", authListener);
    async function setResumeCookie() {
      const cookieValue = encodeURIComponent(`AWARE - /aware`);
      const resumeCookie = Object.assign(cookieAttributes(), { name: 'platform_resume' }, { value: cookieValue });
      await cookieStore.set(resumeCookie);
    }
    setResumeCookie();

    return () => {
      mounted = false;
      try { Hub.remove("auth", authListener); } catch (_) {}
    };
  }, []);

  async function init() {
    if (!ready) return;
    if (initInFlight.current) return;
    initInFlight.current = true;

    await Auth.currentAuthenticatedUser();

    try {
      const initialRequests = await Promise.allSettled([
        API.get("AccountGatewayApi", "/profile"),
        API.get("CommunityApi", `/communities`),
        API.get("MonitoringServiceApi", `/monitors`),
      ]);

      const profileVal = initialRequests[0].status === "fulfilled" ? initialRequests[0].value : null;
      const accountsDetails =
        profileVal?.accounts
          ? await Promise.allSettled(
              profileVal.accounts.map((account) => API.get("AccountGatewayApi", `/account/${account.id}`))
            )
          : [];

      const accountsUsers = sortBy(
        accountsDetails
          .filter((r) => r.status === "fulfilled")
          .map((r) => r.value?.profiles || [])
          .flat(),
        [(obj) => (obj?.name?.toUpperCase() || obj?.email?.toUpperCase())]
      );

      const communitiesResponse =
        initialRequests[1].status === "fulfilled"
          ? sortBy(initialRequests[1].value, [(obj) => obj.name.toUpperCase()])
          : [];

      const monitorApiResponse =
        initialRequests[2].status === "fulfilled"
          ? sortBy(
              initialRequests[2].value.data.reduce((acc, monitor) => {
                monitor.watchers = monitor.subscribers;
                acc.push(monitor);
                return acc;
              }, []),
              [(obj) => obj.name.toLocaleUpperCase()]
            )
          : [];

      const communitiesLookup = communitiesResponse.reduce((acc, val) => {
        acc[val.id] = val;
        return acc;
      }, {});

      const monitorList = monitorApiResponse.map((monitor) => {
        monitor.monitoringcommunity = communitiesLookup[monitor.monitoringcommunity];
        monitor.targetcommunity = communitiesLookup[monitor.targetcommunity];
        return monitor;
      });

      setProfile(profileVal);
      setAvailableCommunities(communitiesResponse);
      setAvailableWatchers(accountsUsers);
      setMonitors(monitorList);
      setPageState(null);
    } catch (err) {
      setPageState(null);
      toast(`There was a problem loading your aware reports`, { type: toast.TYPE.ERROR, autoClose: 3000 });
    } finally {
      initInFlight.current = false;
    }
  }

  useEffect(() => { if (ready) init(); }, [ready]);

  const displayList = useMemo(() => {
    if (!monitors) return [];
    return monitors
      .filter((monitor) =>
        [
          monitor.name || "",
          monitor.reportType,
          monitor.id,
          monitor.monitoringcommunity?.name || "",
          monitor.targetcommunity?.name || "",
          (monitor.watchers || [])
            .map((id) => {
              const w = availableWatchers?.find((aw) => aw.id === id);
              return w ? `${w.name} (${w.email})` : "";
            })
            .join(", "),
          monitor.frequency,
          monitor.isactive ? "Active" : "Inactive",
          translateDateFormat(monitor.modified),
        ]
          .join("")
          .toLowerCase()
          .includes(filterString.toLowerCase())
      )
      .sort((a, b) => {
        if (sortColumn) {
          if (sortDirection === "ascending") return sortKey(a) > sortKey(b) ? 1 : sortKey(a) < sortKey(b) ? -1 : 0;
          else return sortKey(a) > sortKey(b) ? -1 : sortKey(a) < sortKey(b) ? 1 : 0;
        } else return 0;
      })
      .slice(paginationOffset, paginationOffset + paginationSize);
  }, [
    monitors,
    paginationOffset,
    paginationSize,
    filterString,
    sortColumn,
    sortKey,
    sortDirection,
    availableWatchers,
  ]);

  return (
    <>
      <div className="card">
        <div className="card-header text-light font-weight-bold container-fluid" >
          <div className="row">
            <div className="col-12">My Aware Reports</div>
          </div>
        </div>

        <div className="card-body table-responsive">
          <div className="row">
            {monitors && (
              <div className="col-6 pt-2 pl-4">
                <PhGridFilter
                  filterString={filterString}
                  setFilterString={setFilterString}
                  setPaginationOffset={setPaginationOffset}
                />
              </div>
            )}
            <div className="col-6">
              {pageState && (
                <div className="d-inline-block m-3">
                  <b>{pageState} </b>
                  <SpinnerSmall />
                </div>
              )}
              <button
                className="btn btn-primary m-2 float-right"
                onClick={() => { setReportTypeModalActive(true); setModalReportType(null); }}
                disabled={!profile}
              >
                New Aware Report
              </button>
            </div>
          </div>

          <table className="table table-striped table-sm" style={{ padding: 0 }}>
            <thead>
              <tr>
                <th scope="col">
                  <OverlayTrigger placement="top" overlay={<Tooltip>The name you've assigned to your AWARE report</Tooltip>}>
                    <label className="col-form-label p-0">
                      <PhGridSort columnLabel="Name" columnName={(d) => d?.name}
                        config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }} />
                    </label>
                  </OverlayTrigger>
                </th>
                <th scope="col">
                  <OverlayTrigger placement="top" overlay={<Tooltip>What AWARE report you've chosen</Tooltip>}>
                    <label className="col-form-label p-0">
                      <PhGridSort columnLabel="Report Type" columnName={(d) => d?.reportType}
                        config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }} />
                    </label>
                  </OverlayTrigger>
                </th>
                <th scope="col">
                  <OverlayTrigger placement="top" overlay={<Tooltip>A community you've assigned to your AWARE report</Tooltip>}>
                    <label className="col-form-label p-0">
                      <PhGridSort columnLabel="Community 1" columnName={(d) => d?.monitoringcommunity?.name}
                        config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }} />
                    </label>
                  </OverlayTrigger>
                </th>
                <th scope="col">
                  <OverlayTrigger placement="top" overlay={<Tooltip>A community you've assigned to your AWARE report</Tooltip>}>
                    <label className="col-form-label p-0">
                      <PhGridSort columnLabel="Community 2" columnName={(d) => d?.targetcommunity?.name}
                        config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }} />
                    </label>
                  </OverlayTrigger>
                </th>
                <th scope="col">
                  <OverlayTrigger placement="top" overlay={<Tooltip>Users who will receive the AWARE report via email</Tooltip>}>
                    <label className="col-form-label p-0">
                      <PhGridSort columnLabel="Watchers" columnName={(d) => d?.dateCreated}
                        config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }} />
                    </label>
                  </OverlayTrigger>
                </th>
                <th scope="col">
                  <OverlayTrigger placement="top" overlay={<Tooltip>How often the AWARE report will be sent</Tooltip>}>
                    <label className="col-form-label p-0">
                      <PhGridSort columnLabel="Frequency" columnName={(d) => d?.frequency}
                        config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }} />
                    </label>
                  </OverlayTrigger>
                </th>
                <th scope="col">
                  <OverlayTrigger placement="top" overlay={<Tooltip>Active sends on cadence; inactive will not send</Tooltip>}>
                    <label className="col-form-label p-0">
                      <PhGridSort columnLabel="Status" columnName={(d) => d?.isactive}
                        config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }} />
                    </label>
                  </OverlayTrigger>
                </th>
                <th scope="col">
                  <OverlayTrigger placement="top" overlay={<Tooltip>When the AWARE report was last adjusted</Tooltip>}>
                    <label className="col-form-label p-0">
                      <PhGridSort columnLabel="Last Modified" columnName={(d) => d?.modified}
                        config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }} />
                    </label>
                  </OverlayTrigger>
                </th>
                <th scope="col">
                  <OverlayTrigger placement="top" overlay={<Tooltip>Edit or Preview your AWARE report</Tooltip>}>
                    <label className="col-form-label p-0">Action</label>
                  </OverlayTrigger>
                </th>
              </tr>
            </thead>

            <tbody>
              {!monitors && (
                <tr>
                  <td colSpan="7" className="text-center"><SpinnerSmall /></td>
                </tr>
              )}
              {displayList.map((item) => (
                <MonitorRow
                  key={item.id}
                  monitor={item}
                  availableCommunities={availableCommunities}
                  availableWatchers={availableWatchers}
                  init={init}
                  setPageState={setPageState}
                  setPreviewModalActive={setPreviewModalActive}
                  setPreviewModalReport={setPreviewModalReport}
                  modalReportType={item.reportType}
                  setModalReportType={setModalReportType}
                />
              ))}
            </tbody>
          </table>

          {monitors && (
            <div className="row m-0 justify-content-end" style={{ paddingRight: ".75rem" }}>
              <PhGridPagination
                fullList={monitors}
                paginationOffset={paginationOffset}
                setPaginationOffset={setPaginationOffset}
                setPaginationSize={setPaginationSize}
                paginationSize={paginationSize}
                filterString={filterString}
              />
            </div>
          )}
        </div>
      </div>

      {/* Select Report Type Modal */}
      <Modal size="xl" show={reportTypeModalActive} onHide={() => { setReportTypeModalActive(false); }}>
        <Modal.Header closeButton>
          <Modal.Title>Select Report Type to Create</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <div style={{ marginLeft: "1.5%" }}>
              <h1 style={{ fontSize: "large" }}>
                Once set - the user can identify watchers from their account and a monthly or quarterly cadence for the
                watchers to receive an interactive dashboard in their email with the freshest data
              </h1>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              {/* Card 1: P2P */}
              <div style={{ display: "flex", flexFlow: "column", width: "31%", outline: "1px solid black", margin: "1%", alignSelf: "stretch" }}>
                <div style={{ textAlign: "center", margin: "5%" }}>
                  <PhysicianIcon /><ArrowIcon /><PhysicianIcon />
                </div>
                <div style={{ margin: "2.5%" }}>
                  <h1 style={{ fontSize: "22px", textAlign: "center" }}>Physician Community to Physician Community Report</h1>
                  <br />
                  <p style={{ textAlign: "center" }}>
                    Identify a community of physicians from TEAM and compare it to itself or another community to measure
                    market share and volume month over month.
                  </p>
                </div>
                {modalReportType !== "Physician to Physician" ? (
                  <div style={{ margin: "auto auto 0 auto", width: "maxContent" }}>
                    <button className="btn btn-primary m-2" style={{ width: "100%" }}
                      onClick={() => setModalReportType("Physician to Physician")}>
                      Select
                    </button>
                  </div>
                ) : <SelectedButton />}
              </div>

              {/* Card 2: P2F */}
              <div style={{ display: "flex", flexFlow: "column", width: "31%", outline: "1px solid black", margin: "1%", alignSelf: "stretch" }}>
                <div style={{ textAlign: "center", margin: "4.5%" }}>
                  <PhysicianIcon /><ArrowIcon /><FacilityIcon />
                </div>
                <div style={{ margin: "2.5%" }}>
                  <h1 style={{ fontSize: "22px", textAlign: "center" }}>Physician Community to Facility Community Report</h1>
                  <br />
                  <p style={{ textAlign: "center" }}>
                    Identify a physician community and a facility community to measure market share and volume month over month.
                  </p>
                </div>
                {modalReportType !== "Physician to Facility" ? (
                  <div style={{ margin: "auto auto 0 auto", width: "maxContent" }}>
                    <button className="btn btn-primary m-2" style={{ width: "100%" }}
                      onClick={() => setModalReportType("Physician to Facility")}>
                      Select
                    </button>
                  </div>
                ) : <SelectedButton />}
              </div>

              {/* Card 3: F2F */}
              <div style={{ display: "flex", flexFlow: "column", width: "31%", outline: "1px solid black", margin: "1%", alignSelf: "stretch" }}>
                <div style={{ textAlign: "center", margin: "3%", marginTop: "4.5%" }}>
                  <FacilityIcon /><ArrowIcon /><FacilityIcon />
                </div>
                <div style={{ margin: "2.5%" }}>
                  <h1 style={{ fontSize: "22px", textAlign: "center" }}>Facility Community to Facility Community Report</h1>
                  <br />
                  <p style={{ textAlign: "center" }}>
                    Identify a facility community and compare it to itself or another facility community to measure market share and volume month over month.
                  </p>
                </div>
                {modalReportType !== "Facility to Facility" ? (
                  <div style={{ margin: "auto auto 0 auto", width: "maxContent" }}>
                    <button className="btn btn-primary m-2" style={{ width: "100%" }}
                      onClick={() => setModalReportType("Facility to Facility")}>
                      Select
                    </button>
                  </div>
                ) : <SelectedButton />}
              </div>
            </div>

            <div>
              <button
                className="btn btn-success m-2 float-right"
                onClick={() => { setReportTypeModalActive(false); setCreationModalActive(true); }}
                disabled={!modalReportType}
              >
                Proceed
              </button>
              <button
                className="btn btn-danger m-2 float-right"
                onClick={() => { setReportTypeModalActive(false); setModalReportType("Physician to Facility"); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Create Modal */}
      <Modal size="xl" show={creationModalActive} onHide={() => { setCreationModalActive(false); }}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Aware Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <MonitorForm
            availableCommunities={availableCommunities}
            availableWatchers={availableWatchers}
            modalMonitor={modalMonitor}
            setModalMonitor={setModalMonitor}
            modalTarget={modalTarget}
            setModalTarget={setModalTarget}
            modalWatchers={modalWatchers}
            setModalWatchers={setModalWatchers}
            modalFrequency={modalFrequency}
            setModalFrequency={setModalFrequency}
            modalMonitoringServiceName={modalMonitoringServiceName}
            setModalMonitoringServiceName={setModalMonitoringServiceName}
            setFormContainerOpen={setCreationModalActive}
            init={init}
            setPageState={setPageState}
            modalReportType={modalReportType}
            setModalReportType={setModalReportType}
            setReportTypeModalActive={setReportTypeModalActive}
            profile={profile}
          />
        </Modal.Body>
      </Modal>

      {/* Preview Modal */}
      <Modal size="lg" show={previewModalActive} onHide={() => { setPreviewModalActive(false); }}>
        <Modal.Header closeButton>
          <Modal.Title>Send Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Clicking the preview button will send <strong>{previewModalReport?.name}</strong> to the
            email tied to your account <strong>{profile && profile.email}</strong>.
          </p>
        </Modal.Body>
        <Modal.Footer>
          {previewModalActive === "rejected" && <p className="text-danger">There was an error requesting the preview</p>}
          {previewModalActive !== "rejected" && (
            <button
              type="button"
              className="btn btn-primary font-weight-bold"
              onClick={async () => {
                setPreviewModalActive("requesting");
                try {
                  await Auth.currentAuthenticatedUser();
                  await API.post("MonitoringServiceApi", `/ondemand`, { body: { ids: [previewModalReport.id] } });
                  setPreviewModalActive(false);
                } catch (failure) {
                  console.log("failure", failure);
                  setPreviewModalActive("rejected");
                }
              }}
            >
              {`Send me a preview`} {previewModalActive === "requesting" ? <SpinnerSmall color="light" /> : ""}
            </button>
          )}
          <button type="button" className="btn btn-secondary font-weight-bold" onClick={() => { setPreviewModalActive(false); }}>
            {previewModalActive === "rejected" ? "Close" : "Cancel"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default MonitoringServicePage;
